import { useLanguage } from '../i18n/LanguageContext';
import { useCallback, useState, useEffect } from 'react';
import './CouponsPage.css';
import { createCoupon, fetchCoupons, registerCouponToUser } from '../api/coupons';
import Pagination from '../components/Pagination';
import CouponSerialInput from '../components/CouponSerialInput';
import { can } from '../utils/permissions';

const PAGE_SIZE = 50;

function CouponsPage() {
  const { t } = useLanguage();
  const canBulkCreateCoupon = can('coupon.bulk.create');
  const canRegisterCoupon = can('coupon.register');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [totalCoupons, setTotalCoupons] = useState(0);
  const [registeredCoupons, setRegisteredCoupons] = useState(0);
  const [nonRegisteredCoupons, setNonRegisteredCoupons] = useState(0);
  const [loading, setLoading] = useState(true);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [registerUserUuid, setRegisterUserUuid] = useState('');
  const [registerSerial, setRegisterSerial] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkCsvFile, setBulkCsvFile] = useState(null);
  const [bulkDragActive, setBulkDragActive] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'registered' | 'unregistered'
  const [currentPage, setCurrentPage] = useState(1);

  const loadCoupons = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * PAGE_SIZE;
      const res = await fetchCoupons({
        offset,
        limit: PAGE_SIZE,
        search: searchTerm.trim(),
      });
      const nextCoupons = res.data.coupons || [];
      setCoupons(nextCoupons);
      setTotalCoupons(Number(res.data.total ?? 0));
      setRegisteredCoupons(Number(res.data.registered ?? 0));
      setNonRegisteredCoupons(Number(res.data.nonRegistered ?? 0));
      setSelectedCoupon((prev) => (
        prev ? nextCoupons.find((coupon) => coupon.uuid === prev.uuid) || prev : prev
      ));
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
      setCoupons([]);
      setTotalCoupons(0);
      setRegisteredCoupons(0);
      setNonRegisteredCoupons(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput.trim());
    setCurrentPage(1);
  };

  const getWalletUuid = (coupon) => coupon.wallet?.uuid || coupon.walletUuid || '';
  const getWalletHolder = (coupon) => coupon.wallet?.holder || coupon.walletHolder || '';
  const getWalletHolderUuid = (coupon) => coupon.wallet?.holderUuid || coupon.walletHolderUuid || '';

  // 검색 + 상태 필터
  const filteredCoupons = coupons.filter((c) => {
    const isRegistered = Boolean(getWalletUuid(c));

    // 상태 필터
    if (statusFilter === 'registered' && !isRegistered) return false;
    if (statusFilter === 'unregistered' && isRegistered) return false;

    return true;
  });

  // 쿠폰 상태 헬퍼
  const getCouponStatus = (c) => {
    if (getWalletUuid(c)) return 'registered';
    if (c.expire && new Date(c.expire) < new Date()) return 'expired';
    return 'unregistered';
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'registered':
        return <span className="status-badge registered">{t("등록됨")}</span>;
      case 'unregistered':
        return <span className="status-badge unregistered">{t("미등록")}</span>;
      case 'expired':
        return <span className="status-badge expired">{t("만료")}</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateString, withTime = false) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    const datePart = `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}`;
    if (withTime) {
      return `${datePart} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
    return datePart;
  };

  const formatCredit = (credit) => {
    if (credit === null || credit === undefined || credit === '') return '-';
    const numericCredit = Number(credit);
    return Number.isFinite(numericCredit) ? numericCredit.toLocaleString() : String(credit);
  };

  const getRegisterErrorMessage = (err) => (
    err?.response?.data?.message || err?.message || '쿠폰 등록에 실패했습니다.'
  );

  const getPaginationInfo = () => {
    if (searchTerm || statusFilter !== 'all') {
      return `현재 페이지 필터 결과 ${filteredCoupons.length.toLocaleString()}건 / 전체 ${totalCoupons.toLocaleString()}건`;
    }

    if (totalCoupons === 0) return '총 0건 표시';

    const start = (currentPage - 1) * PAGE_SIZE + 1;
    const end = Math.min(start + coupons.length - 1, totalCoupons);
    return `총 ${totalCoupons.toLocaleString()}건 중 ${start.toLocaleString()}-${end.toLocaleString()} 표시`;
  };

  const closeRegisterModal = () => {
    if (registerLoading) return;
    setRegisterModalOpen(false);
    setRegisterError('');
  };

  const openBulkModal = () => {
    if (!canBulkCreateCoupon) return;
    setBulkCsvFile(null);
    setBulkDragActive(false);
    setBulkLoading(false);
    setBulkModalOpen(true);
  };

  const closeBulkModal = () => {
    if (bulkLoading) return;
    setBulkDragActive(false);
    setBulkModalOpen(false);
  };

  const handleBulkFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setBulkCsvFile(file);
  };

  const handleBulkFileDrop = (event) => {
    event.preventDefault();
    setBulkDragActive(false);
    const file = event.dataTransfer.files?.[0] || null;
    if (file) setBulkCsvFile(file);
  };

  const parseBulkCouponCsv = async (file) => {
    const text = await file.text();
    const rows = [];

    text.split(/\r?\n/).forEach((rawLine, index) => {
      const lineNumber = index + 1;
      const line = rawLine.replace(/^\uFEFF/, '').trim();

      if (!line) return;

      const columns = line.split(',').map((value) => value.trim());
      const [serialNumber = '', creditText = '', payableText = ''] = columns;
      const lowerColumns = columns.map((value) => value.toLowerCase());

      if (
        rows.length === 0 &&
        columns.length === 3 &&
        lowerColumns[0] === 'serialnumber' &&
        lowerColumns[1] === 'credit' &&
        lowerColumns[2] === 'payable'
      ) {
        return;
      }

      if (columns.length !== 3 || !serialNumber || !creditText || !payableText) {
        throw new Error(`${lineNumber}번째 줄의 CSV 형식이 올바르지 않습니다.\n시리얼 번호: ${serialNumber || '-'}`);
      }

      const credit = Number(creditText);
      const payable = Number(payableText);

      if (!Number.isFinite(credit) || credit <= 0) {
        throw new Error(`${lineNumber}번째 줄의 credit 값이 올바르지 않습니다.\n시리얼 번호: ${serialNumber}`);
      }
      if (!Number.isInteger(payable) || ![0, 1].includes(payable)) {
        throw new Error(`${lineNumber}번째 줄의 payable 값은 0 또는 1이어야 합니다.\n시리얼 번호: ${serialNumber}`);
      }

      rows.push({
        lineNumber,
        serialNumber,
        credit,
        payable,
      });
    });

    return rows;
  };

  const handleBulkRegisterCoupons = async () => {
    if (!canBulkCreateCoupon) return;
    if (!bulkCsvFile || bulkLoading) return;

    try {
      setBulkLoading(true);

      const rows = await parseBulkCouponCsv(bulkCsvFile);
      if (rows.length === 0) {
        alert('등록할 쿠폰 데이터가 없습니다.');
        return;
      }

      for (const row of rows) {
        try {
          await createCoupon({
            serialNumber: row.serialNumber,
            credit: row.credit,
            payable: row.payable,
          });
        } catch (err) {
          const message = err?.response?.data?.message || err?.message || '쿠폰 생성에 실패했습니다.';
          alert(`쿠폰 일괄 등록이 중단되었습니다.\n라인 번호: ${row.lineNumber}\n쿠폰 시리얼 번호: ${row.serialNumber}\n사유: ${message}`);
          return;
        }
      }

      alert(`${rows.length.toLocaleString()}건의 쿠폰을 등록했습니다.`);
      setBulkModalOpen(false);
      setBulkCsvFile(null);
      await loadCoupons();
    } catch (err) {
      alert(err?.message || 'CSV 파일을 처리하지 못했습니다.');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleRegisterCoupon = async () => {
    if (!canRegisterCoupon) return;
    if (!registerSerial || !registerUserUuid) return;

    try {
      setRegisterLoading(true);
      setRegisterError('');
      await registerCouponToUser({
        serialNumber: registerSerial.trim(),
        userUuid: registerUserUuid.trim(),
      });
      await loadCoupons();
      setRegisterModalOpen(false);
      setRegisterSerial('');
      setRegisterUserUuid('');
    } catch (err) {
      console.error('Failed to register coupon:', err);
      setRegisterError(getRegisterErrorMessage(err));
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="coupons-page">
      {/* 헤더 */}
      <div className="page-header">
        <div>
          <h2 className="page-title">{t("쿠폰 관리")}</h2>
          <p className="page-subtitle">{t("쿠폰 등록 현황을 조회하고, 사용자에게 쿠폰을 등록할 수 있습니다.")}</p>
        </div>
        <div className="header-actions">
          <button
            className="btn-bulk-register"
            disabled={!canBulkCreateCoupon}
            title={!canBulkCreateCoupon ? '쿠폰 대량 생성 권한이 없습니다.' : undefined}
            onClick={openBulkModal}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="12" y1="18" x2="12" y2="12"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
            {t("쿠폰 일괄 등록")}
          </button>
          <div className="action-row">
            <form className="search-form" onSubmit={handleSearchSubmit}>
              <div className="search-box">
                <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  type="text"
                  placeholder={t("시리얼넘버 입력 후 Enter")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <button className="btn-search" type="submit" disabled={loading}>
                {t("검색")}
              </button>
            </form>
            <button
              className="btn-register"
              disabled={!canRegisterCoupon}
              title={!canRegisterCoupon ? '쿠폰 사용자 등록 권한이 없습니다.' : undefined}
              onClick={() => {
                if (!canRegisterCoupon) return;
                setRegisterUserUuid('');
                setRegisterSerial('');
                setRegisterError('');
                setRegisterModalOpen(true);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {t("쿠폰 사용자 등록")}
            </button>
          </div>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="stats-row">
        <div className={`stat-card clickable ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}>
          <p className="stat-label">{t("전체 쿠폰")}</p>
          <p className="stat-value">{totalCoupons}</p>
        </div>
        <div className={`stat-card clickable ${statusFilter === 'registered' ? 'active' : ''}`} onClick={() => { setStatusFilter('registered'); setCurrentPage(1); }}>
          <p className="stat-label">{t("등록됨")}</p>
          <p className="stat-value">{registeredCoupons}</p>
        </div>
        <div className={`stat-card clickable ${statusFilter === 'unregistered' ? 'active' : ''}`} onClick={() => { setStatusFilter('unregistered'); setCurrentPage(1); }}>
          <p className="stat-label">{t("미등록")}</p>
          <p className="stat-value">{nonRegisteredCoupons}</p>
        </div>
      </div>

      {/* 테이블 */}
      <div className="table-card">
        <table className="coupons-table">
          <thead>
            <tr>
              <th>{t("시리얼 넘버")}</th>
              <th>{t("크레딧")}</th>
              <th>{t("유/무료")}</th>
              <th>{t("등록 상태")}</th>
              <th>{t("생성일")}</th>
              <th>{t("만료일")}</th>
              <th>Description</th>
              <th style={{ textAlign: 'center' }}>{t("관리")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>{t("데이터를 불러오는 중입니다...")}</td></tr>
            ) : filteredCoupons.length > 0 ? (
              filteredCoupons.map(coupon => {
                const status = getCouponStatus(coupon);
                const isExpired = status === 'expired';

                return (
                  <tr
                    key={coupon.uuid}
                    className={`coupon-row ${isExpired ? 'expired' : ''}`}
                    onClick={() => setSelectedCoupon(coupon)}
                  >
                    <td className="serial-text">{coupon.serialNumber}</td>
                    <td className="credit-text">{formatCredit(coupon.credit)}</td>
                    <td>
                      {coupon.payable
                        ? <span className="status-badge paid">{t("유료")}</span>
                        : <span className="status-badge free">{t("무료")}</span>}
                    </td>
                    <td>{renderStatusBadge(status)}</td>
                    <td className="detail-text">{formatDate(coupon.createdAt)}</td>
                    <td className="detail-text">{formatDate(coupon.expire)}</td>
                    <td className="detail-text" style={{ fontSize: '13px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {coupon.description || '-'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button className="action-btn" onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCoupon(coupon);
                      }}>
                        {t("상세 보기")}
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
                  "{searchTerm}" 에 대한 검색 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          currentPage={currentPage}
          total={totalCoupons}
          pageSize={PAGE_SIZE}
          info={getPaginationInfo()}
          disabled={loading}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Drawer Overlay */}
      {selectedCoupon && (
        <div className="drawer-overlay" onClick={() => setSelectedCoupon(null)}></div>
      )}

      {/* Drawer Panel */}
      <div className={`detail-drawer ${selectedCoupon ? 'open' : ''}`}>
        {selectedCoupon && (() => {
          const status = getCouponStatus(selectedCoupon);
          const walletUuid = getWalletUuid(selectedCoupon);
          const walletHolder = getWalletHolder(selectedCoupon);
          const walletHolderUuid = getWalletHolderUuid(selectedCoupon);

          return (
            <>
              <div className="drawer-header">
                <h3>{t("쿠폰 상세 정보")}</h3>
                <button className="drawer-close-btn" onClick={() => setSelectedCoupon(null)}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              </div>

              <div className="drawer-content">
                {/* 시리얼 넘버 요약 */}
                <div className="drawer-profile-card">
                  <div className="drawer-profile-info">
                    <div className="drawer-model">
                      <span className="drawer-label">{t("시리얼 넘버")}</span>
                    </div>
                    <div className="drawer-name">
                      {selectedCoupon.serialNumber}
                      <span>{renderStatusBadge(status)}</span>
                    </div>
                    <div className="drawer-model" style={{ marginTop: '8px' }}>
                      <span className="drawer-label">{t("크레딧")}</span>
                      {formatCredit(selectedCoupon.credit)}
                      {selectedCoupon.payable
                        ? <span className="status-badge paid" style={{ marginLeft: '8px' }}>{t("유료")}</span>
                        : <span className="status-badge free" style={{ marginLeft: '8px' }}>{t("무료")}</span>}
                    </div>
                  </div>
                </div>

                {/* 쿠폰 기본 정보 */}
                <div className="drawer-section">
                  <h4>{t("쿠폰 기본 정보")}</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>{t("시리얼 넘버")}</label>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{selectedCoupon.serialNumber}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("크레딧")}</label>
                      <span style={{ fontWeight: 700 }}>{formatCredit(selectedCoupon.credit)}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("유/무료 (Payable)")}</label>
                      <span>{selectedCoupon.payable ? t("유료") : t("무료")}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("설명")}</label>
                      <span>{selectedCoupon.description || '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("만료일 (Expire)")}</label>
                      <span>{formatDate(selectedCoupon.expire, true)}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("생성일 (CreatedAt)")}</label>
                      <span>{formatDate(selectedCoupon.createdAt, true)}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("수정일 (UpdatedAt)")}</label>
                      <span>{formatDate(selectedCoupon.updatedAt, true)}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("고유 식별자 (UUID)")}</label>
                      <span style={{ fontSize: '11px', color: '#6b7280' }}>{selectedCoupon.uuid}</span>
                    </div>
                  </div>
                </div>

                {/* 등록 정보 (Wallet) */}
                <div className="drawer-section">
                  <h4>{t("등록 정보 (Wallet 연결)")}</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>{t("등록 상태")}</label>
                      <span>{renderStatusBadge(status)}</span>
                    </div>
                    <div className="info-item">
                      <label>Wallet UUID</label>
                      <span style={{ fontSize: '11px', color: '#6b7280' }}>{walletUuid || '-'}</span>
                    </div>
                    {(walletHolder || walletHolderUuid) && (
                      <>
                        <div className="info-item">
                          <label>Wallet Holder</label>
                          <span>{walletHolder || '-'}</span>
                        </div>
                        <div className="info-item">
                          <label>Holder UUID</label>
                          <span style={{ fontSize: '11px', color: '#6b7280' }}>{walletHolderUuid || '-'}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* 관리자 액션: 미등록 쿠폰만 표시 */}
                {!getWalletUuid(selectedCoupon) && (
                  <div className="drawer-section">
                    <h4>{t("쿠폰 관리 (관리자 기능)")}</h4>

                    {status !== 'expired' ? (
                      <div className="admin-action-card">
                        <div className="action-text">
                          <strong>{t("사용자에게 등록")}</strong>
                        <p>{canRegisterCoupon ? '이 쿠폰을 특정 사용자의 Wallet에 연결합니다.' : '쿠폰 사용자 등록 권한이 없습니다.'}</p>
                      </div>
                        <button className="btn-secondary" disabled={!canRegisterCoupon} onClick={() => {
                          if (!canRegisterCoupon) return;
                          setRegisterSerial(selectedCoupon.serialNumber);
                          setRegisterUserUuid('');
                          setRegisterError('');
                          setRegisterModalOpen(true);
                        }}>{t("등록")}</button>
                      </div>
                    ) : (
                      <div className="admin-action-card" style={{ background: '#f9fafb', opacity: 0.7 }}>
                        <div className="action-text">
                          <strong style={{ color: '#6b7280' }}>{t("만료된 쿠폰")}</strong>
                          <p>{t("이 쿠폰은 만료되어 등록할 수 없습니다.")}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </div>

      {/* 쿠폰 일괄 등록 모달 */}
      {bulkModalOpen && (
        <>
          <div className="modal-overlay" onClick={closeBulkModal}></div>
          <div className="modal bulk-coupon-modal">
            <div className="modal-header">
              <div>
                <h3>{t("쿠폰 일괄 등록")}</h3>
                <p className="modal-subtitle">시리얼 넘버 CSV에 공통 설정을 적용해 쿠폰을 생성합니다.</p>
              </div>
              <button className="drawer-close-btn" onClick={closeBulkModal}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <label
                className={`csv-upload-box ${bulkDragActive ? 'drag-active' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setBulkDragActive(true);
                }}
                onDragLeave={() => setBulkDragActive(false)}
                onDrop={handleBulkFileDrop}
              >
                <input type="file" accept=".csv,text/csv" onChange={handleBulkFileChange} disabled={bulkLoading} />
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <path d="M12 18v-6"></path>
                  <path d="m9 15 3-3 3 3"></path>
                </svg>
                <strong>{bulkCsvFile ? bulkCsvFile.name : 'CSV 파일 선택 또는 드래그 앤 드롭'}</strong>
                <span>각 줄은 serialNumber, credit, payable 형식이어야 합니다.</span>
              </label>

              <div className="bulk-policy-box">
                <strong>{t("CSV 입력 규칙")}</strong>
                <ul>
                  <li>한 줄에 쿠폰 1개를 입력합니다: serialNumber, credit, payable</li>
                  <li>payable은 무료 0, 유료 1로 입력합니다.</li>
                  <li>오류가 발생하면 해당 라인에서 등록을 중단합니다.</li>
                  <li>첫 줄이 serialNumber,credit,payable 헤더이면 자동으로 건너뜁니다.</li>
                </ul>
              </div>

              <p className="modal-warning">
                등록 중 오류가 발생하면 라인 번호와 쿠폰 시리얼 번호를 표시하고 처리를 멈춥니다.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" disabled={bulkLoading} onClick={closeBulkModal}>{t("취소")}</button>
              <button
                className="btn-primary"
                disabled={!bulkCsvFile || bulkLoading || !canBulkCreateCoupon}
                onClick={handleBulkRegisterCoupons}
              >
                {bulkLoading ? t("등록 중...") : t("등록")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* 쿠폰 사용자 등록 모달 */}
      {registerModalOpen && (
        <>
          <div className="modal-overlay" onClick={closeRegisterModal}></div>
          <div className="modal">
            <div className="modal-header">
              <h3>{t("쿠폰 사용자 등록")}</h3>
              <button className="drawer-close-btn" onClick={closeRegisterModal}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label>{t("쿠폰 시리얼넘버")}</label>
                <CouponSerialInput
                  placeholder="X4H5-ED2G-A39S-EE6Q"
                  value={registerSerial}
                  onValueChange={setRegisterSerial}
                  disabled={registerLoading}
                />
              </div>
              <div className="modal-field">
                <label>{t("사용자 UUID")}</label>
                <input
                  type="text"
                  placeholder="75376934-5990-424b-..."
                  value={registerUserUuid}
                  onChange={(e) => setRegisterUserUuid(e.target.value)}
                  disabled={registerLoading}
                />
              </div>
              <p className="modal-warning">
                해당 쿠폰이 사용자의 Wallet에 등록됩니다. 시리얼넘버와 사용자 UUID를 정확히 확인하세요.
              </p>
              {registerError && <p className="modal-error">{registerError}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" disabled={registerLoading} onClick={closeRegisterModal}>{t("취소")}</button>
              <button
                className="btn-primary"
                disabled={registerLoading || !registerSerial || !registerUserUuid || !canRegisterCoupon}
                onClick={handleRegisterCoupon}
              >
                {registerLoading ? t("등록 중...") : t("등록")}
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
}

export default CouponsPage;
